import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../Modal';
import MultiSelect, { MultiSelectOption, SelectId } from '../MultiSelect';
import { FormField, Button, ConfirmModal } from '../ui';
import { api } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import { formatEventOption, sortEventsByDate } from '../../utils/eventUtils';
import './BroadcastModal.css';
import { translateError } from '../../utils/errorMessages';

interface BroadcastModalProps {
  isOpen: boolean;
  onClose: (sent?: boolean) => void;
  preselectedEventId?: string;
  preselectedEventName?: string;
  preselectedGroups?: string[];
}

const BroadcastModal: React.FC<BroadcastModalProps> = ({
  isOpen,
  onClose,
  preselectedEventId,
  preselectedEventName,
  preselectedGroups,
}) => {
  const { t, i18n } = useTranslation();
  const { addToast } = useToast();
  const [eventOptions, setEventOptions] = useState<MultiSelectOption[]>([]);
  const [groupOptions, setGroupOptions] = useState<MultiSelectOption[]>([]);
  const [selectedEvents, setSelectedEvents] = useState<SelectId[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<SelectId[]>([]);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  // Store preselected values in refs to avoid dependency array issues
  const preselectedGroupsRef = React.useRef(preselectedGroups);
  preselectedGroupsRef.current = preselectedGroups;

  const fetchOptions = useCallback(async () => {
    setLoading(true);
    try {
      const [eventsRes, groupsRes] = await Promise.all([
        api.events.list(),
        api.groups.list(),
      ]);
      // Show all events sorted by date, with dates in the label
      const sorted = sortEventsByDate(eventsRes.data as any[]);
      setEventOptions(
        sorted.map((e) => ({ _id: e._id, name: formatEventOption(e, i18n.language) }))
      );
      setGroupOptions(
        (groupsRes.data as any[]).map((g) => ({ _id: g._id, name: g.name }))
      );
    } catch (err) {
      console.error('Failed to fetch broadcast options:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset form state when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchOptions();
      setSelectedEvents([]);
      setSelectedGroups(preselectedGroupsRef.current || []);
      setMessage('');
      setDiscardConfirmOpen(false);
    }
  }, [isOpen, fetchOptions]);

  // A typed-but-unsent broadcast is lost on close, so confirm first.
  const isDirty = message.trim().length > 0 || selectedEvents.length > 0;

  const requestClose = (): boolean | void => {
    if (!isDirty) return;
    setDiscardConfirmOpen(true);
    return false;
  };

  // All targeted event IDs: preselected (from event detail) + manually picked
  const allEventIds = preselectedEventId
    ? [preselectedEventId, ...selectedEvents]
    : selectedEvents;

  const hasTarget = allEventIds.length > 0 || selectedGroups.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!message.trim()) {
      addToast(t('announcements.validation.messageRequired'), 'error');
      return;
    }

    if (!hasTarget) {
      addToast(t('announcements.validation.targetRequired'), 'error');
      return;
    }

    setSending(true);
    try {
      const res = await api.announcements.create({
        message: message.trim(),
        events: allEventIds,
        groups: selectedGroups,
      });
      const recipientCount = (res.data as { recipientCount?: number } | undefined)?.recipientCount;
      addToast(
        typeof recipientCount === 'number'
          ? t('announcements.sentToCount', { count: recipientCount })
          : t('announcements.sendSuccess'),
        'success',
      );
      onClose(true);
    } catch (err: any) {
      addToast(translateError(err, t, 'announcements.sendError'), 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={() => onClose()}
      onRequestClose={requestClose}
      dismissible={!sending}
      title={t('announcements.newBroadcast')}
      size="medium"
    >
      {loading ? (
        <div className="broadcast-modal__loading">
          <div className="broadcast-modal__spinner"></div>
        </div>
      ) : (
        <form className="broadcast-modal" onSubmit={handleSubmit}>
          {preselectedEventId && preselectedEventName && (
            <div className="broadcast-modal__section">
              <label className="broadcast-modal__label">{t('announcements.event')}</label>
              <div className="broadcast-modal__event-tag">
                {preselectedEventName}
              </div>
            </div>
          )}

          <div className="broadcast-modal__section">
            <MultiSelect
              id="broadcast-events"
              label={t('announcements.selectEvents')}
              options={eventOptions}
              selectedIds={selectedEvents}
              onChange={setSelectedEvents}
              placeholder={t('announcements.selectEvents')}
            />
          </div>

          <div className="broadcast-modal__section">
            <MultiSelect
              id="broadcast-groups"
              label={t('announcements.selectGroups')}
              options={groupOptions}
              selectedIds={selectedGroups}
              onChange={setSelectedGroups}
              placeholder={t('announcements.selectGroups')}
            />
            {selectedGroups.length > 0 && (
              <small className="form-help-text">{t('announcements.groupSnapshotNote')}</small>
            )}
          </div>

          <div className="broadcast-modal__section">
            <FormField htmlFor="broadcast-message" label={t('announcements.message')}>
              <textarea
                id="broadcast-message"
                className="broadcast-modal__textarea"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('announcements.messagePlaceholder')}
                rows={4}
                maxLength={2000}
                disabled={sending}
              />
            </FormField>
          </div>

          <div className="broadcast-modal__actions">
            <Button
              variant="secondary"
              onClick={() => (isDirty ? setDiscardConfirmOpen(true) : onClose())}
              disabled={sending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={sending}
              disabled={sending || !message.trim() || !hasTarget}
            >
              {sending ? t('common.sending') : t('announcements.sendBroadcast')}
            </Button>
          </div>
        </form>
      )}
    </Modal>
    <ConfirmModal
      isOpen={discardConfirmOpen}
      onClose={() => setDiscardConfirmOpen(false)}
      onConfirm={() => {
        setDiscardConfirmOpen(false);
        onClose();
      }}
      title={t('unsavedChanges.title')}
      message={t('unsavedChanges.message')}
      confirmText={t('unsavedChanges.discard')}
      cancelText={t('unsavedChanges.keepEditing')}
      variant="danger"
    />
    </>
  );
};

export default BroadcastModal;
